import clsx from 'clsx';
import { DetailedHTMLProps, InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { useUnfortunateWayToAppendCurrency } from '@portal/state/hooks/useUnfortunateWayToAppendCurrency';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { useStoreValue } from 'state/hooks/useStore';
import { useStoreCurrency } from 'state/hooks/useStoreCurrency';
import { SECOND_STEP_NAME } from '../../constants';
import { useBabSanity } from '../../hooks';
import { ProductBaBFinalOption } from '../../types';
import { getStoreProductVariantId } from '../../utils';
import styles from './SecondStepInput.module.scss';

interface Props extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  product: ProductBaBFinalOption;
  isOneTime: boolean;
  isDouble: boolean;
  basePrice?: number;
  price: number | string;
  pricePerServing?: string;
  priceTextMobile?: string;
  priceTextDesktop?: string;
}

export const SecondStepInput = ({
  id,
  name,
  checked,
  onChange,
  product,
  basePrice,
  price,
  pricePerServing,
  priceTextMobile,
  priceTextDesktop,
  isOneTime,
  isDouble
}: Props) => {
  const { theme, oldPriceFirstToggle: oldPriceFirst } = useBabSanity();
  const { symbolCurrency } = useStoreCurrency();
  const { productTitle, cardBadge, description, descriptionAdditional, ProductIds } = product;
  const store = useStoreValue();
  const { t } = useTranslation('buildBundle');
  const { conditionallyAppendUSDtoPrice, shouldAppendUSD } = useUnfortunateWayToAppendCurrency();

  const productVariantId = getStoreProductVariantId(ProductIds, store);
  const inputId = id || `${SECOND_STEP_NAME}-${productVariantId}`;

  const isFamilyPlanDesign = theme === 'family_plan';

  return (
    <label
      htmlFor={id}
      className={clsx(styles.card, {
        [styles.cardOneTime]: isOneTime,
        [styles.cardChecked]: checked,
        [styles.cardCheckedOneTime]: checked && isOneTime
      })}>
      <input
        type="radio"
        name={name}
        id={inputId}
        value={productVariantId}
        className={styles.input}
        onChange={onChange}
      />
      {cardBadge && (
        <div
          className={clsx(styles.badge, {
            [styles.badgeDouble]: isDouble,
            [styles.badgeNone]: isOneTime
          })}>
          {cardBadge}
        </div>
      )}
      <div className={clsx(styles.main, isOneTime ? styles.mainOneTime : styles.mainSubscription)}>
        <div className={styles.checkmarkWrap}>
          <div className={styles.checkmark} />
        </div>

        {isOneTime && (
          <div
            className={clsx(styles.contentOneTime, {
              [styles.contentOneTimeNew]: isFamilyPlanDesign
            })}>
            <span
              className={clsx(styles.priceTitle, {
                [styles.USDEnabledText]: shouldAppendUSD(price)
              })}>
              {productTitle}
            </span>
            <div className={styles.priceWrapper}>
              <span
                className={clsx(styles.priceValue, {
                  [styles.USDEnabledText]: shouldAppendUSD(price)
                })}>
                {symbolCurrency}
                {conditionallyAppendUSDtoPrice(price)}
              </span>
              {descriptionAdditional && <p className={styles.priceText}>{descriptionAdditional}</p>}
            </div>
          </div>
        )}

        {!isOneTime && (
          <div className={styles.content}>
            <div
              className={clsx(styles.heading, { [styles.headingNewDesign]: isFamilyPlanDesign })}>
              <span
                className={clsx(styles.priceTitle, {
                  [styles.USDEnabledText]: shouldAppendUSD(price)
                })}>
                {productTitle}
              </span>
              <div className={clsx(styles.price, oldPriceFirst && styles.flexRowReverse)}>
                <span
                  className={clsx(styles.priceValue, {
                    [styles.priceValueNewDesign]: isFamilyPlanDesign,
                    [styles.USDEnabledText]: shouldAppendUSD(price)
                  })}>
                  {symbolCurrency}
                  {conditionallyAppendUSDtoPrice(price) as string}
                </span>
                {basePrice && (
                  <span
                    aria-label="original price struck through"
                    className={clsx(styles.priceValue, styles.priceValueOld, {
                      [styles.USDEnabledText]: shouldAppendUSD(price)
                    })}>
                    {symbolCurrency}
                    <s>{basePrice}</s>
                    <sup>†</sup>
                  </span>
                )}
              </div>
            </div>
            <div className={styles.pricePerServingWrapper}>
              {descriptionAdditional && (
                <p
                  aria-label="current price"
                  className={clsx(styles.descriptionAdditional, {
                    [styles.USDEnabledText]: shouldAppendUSD(price)
                  })}>
                  {descriptionAdditional}
                </p>
              )}
              {pricePerServing && (
                <span
                  className={clsx(styles.pricePerServing, {
                    [styles.USDEnabledText]: shouldAppendUSD(pricePerServing)
                  })}>
                  {symbolCurrency}
                  {conditionallyAppendUSDtoPrice(pricePerServing) as string}
                  {priceTextMobile ? (
                    <span className={styles.separateShort}>
                      {' '}
                      {conditionallyAppendUSDtoPrice(priceTextMobile)}
                    </span>
                  ) : (
                    <span className={styles.separateShort}> {t('pricePerServingText.mobile')}</span>
                  )}
                  {priceTextDesktop ? (
                    <span className={styles.separateLong}>
                      {' '}
                      {conditionallyAppendUSDtoPrice(priceTextDesktop)}
                    </span>
                  ) : (
                    <span className={styles.separateLong}> {t('pricePerServingText.desktop')}</span>
                  )}
                </span>
              )}
            </div>

            {description && (
              <CustomPortableText
                value={description}
                className={clsx(styles.description, {
                  [styles.USDEnabledText]: shouldAppendUSD(price)
                })}
              />
            )}
          </div>
        )}
      </div>
    </label>
  );
};
